import { useCallback, useEffect, useMemo, useState } from 'react';
import { env } from '../../config/env';
import { useDailyState } from '../../lib/storage';
import { pickDailyQuote } from '../../lib/quote/dailyQuote';
import { gradientForDay, type QuoteArt } from '../../lib/quote/gradient';
import { generateQuoteImage, isImageDataUrl } from '../../lib/quote/imageGen';
import type { Quote } from '../../lib/quote/quotes';

/**
 * `off`        no VITE_IMAGE_GEN_API_KEY — the designed default, not an error
 * `generating` a request is in flight
 * `ready`      an image is being shown
 * `failed`     generation did not work; the gradient stands in, silently
 */
export type ImagePhase = 'off' | 'generating' | 'ready' | 'failed';

export interface DailyQuote {
  /** 'YYYY-MM-DD', straight from the app's shared midnight clock. */
  readonly dayKey: string;
  readonly quote: Quote;
  readonly art: QuoteArt;
  readonly image: string | null;
  readonly imagePhase: ImagePhase;
  /** The <img> could not decode; drop back to the gradient for good. */
  readonly reportImageBroken: () => void;
}

/**
 * A 1024² PNG is 1.5–2.5 MB of base64, and localStorage is ~5 MB *total*
 * across every panel. Anything over this is kept in memory for the session
 * instead of being written — the image still shows, it just costs one
 * regeneration after a reload rather than starving Bitcoin's price cache.
 * `writeJson` already swallows QuotaExceededError; this stops us relying on
 * that as a routine code path.
 */
const MAX_PERSIST_CHARS = 700_000;

/**
 * Module-level in-flight map, keyed by day.
 *
 * React 18 StrictMode double-mounts every effect in dev, and this effect
 * spends real money. A `useEffect` cleanup cannot help — the second mount
 * starts before the first request returns. Deduping at module scope is the
 * only thing that makes "one image per day" true (DECISIONS.md, cross-cutting
 * note 1). Entries are never evicted on failure either, so a day that fails
 * costs exactly one attempt per page load rather than one per re-render.
 */
const inFlight = new Map<string, Promise<string | null>>();

function requestImage(dayKey: string, quote: Quote, apiKey: string): Promise<string | null> {
  const existing = inFlight.get(dayKey);
  if (existing !== undefined) return existing;
  const started = generateQuoteImage(quote, apiKey);
  inFlight.set(dayKey, started);
  return started;
}

const isCachedImage = (v: unknown): v is string | null => v === null || isImageDataUrl(v);

/**
 * Everything the Quote card needs for today.
 *
 * The quote itself is NOT stored — it is a pure function of the day key, so
 * persisting it would only create a second source of truth that could
 * disagree with the first. Only the generated image is cached, because that
 * one costs money and 5–15 seconds.
 *
 * The day key comes from `useDailyState` rather than from `localDateKey()`,
 * so the card rolls over on exactly the same tick as every other daily panel
 * — including live, in a pinned tab that has been open since yesterday.
 */
export function useQuoteOfTheDay(): DailyQuote {
  const [cachedImage, setCachedImage, dayKey] = useDailyState<string | null>(
    'quote',
    'image',
    null,
    isCachedImage,
  );

  // Oversized images live here instead of in storage. Also the landing spot
  // for a "too big to persist" result, so the card still shows it today.
  const [sessionImage, setSessionImage] = useState<string | null>(null);
  const [broken, setBroken] = useState<string | null>(null);
  const [phase, setPhase] = useState<ImagePhase>(() =>
    env.quote.imageGenApiKey === undefined ? 'off' : 'generating',
  );

  const quote = useMemo(() => pickDailyQuote(dayKey), [dayKey]);
  const art = useMemo(() => gradientForDay(dayKey, quote.text), [dayKey, quote.text]);

  useEffect(() => {
    const apiKey = env.quote.imageGenApiKey;
    if (apiKey === undefined) {
      setPhase('off');
      return;
    }
    if (cachedImage !== null) {
      setPhase('ready');
      return;
    }

    let alive = true;
    setPhase('generating');
    void requestImage(dayKey, quote, apiKey).then((url) => {
      if (!alive) return;
      if (url === null) {
        // Silent by design: the gradient is a complete answer, so there is
        // nothing here the user could act on.
        setPhase('failed');
        return;
      }
      if (url.length <= MAX_PERSIST_CHARS) setCachedImage(url);
      else setSessionImage(url);
      setPhase('ready');
    });

    return () => {
      alive = false;
    };
  }, [dayKey, quote, cachedImage, setCachedImage]);

  // A new day must clear yesterday's in-memory image and any broken marker.
  useEffect(() => {
    setSessionImage(null);
    setBroken(null);
  }, [dayKey]);

  const candidate = cachedImage ?? sessionImage;
  const image = candidate !== null && candidate === broken ? null : candidate;

  const reportImageBroken = useCallback(() => {
    setBroken(candidate);
  }, [candidate]);

  return {
    dayKey,
    quote,
    art,
    image,
    // A decode failure is the same outcome as a generation failure.
    imagePhase: image === null && phase === 'ready' ? 'failed' : phase,
    reportImageBroken,
  };
}
